import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { BaseLegend } from "../../../components/form/legend/styled";
import { AuthLayout } from "../../../containers/layout/auth";
import { PasswordExpiredWrapper, SignInWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseButton } from "../../../components/button/styled";
import { signInUser } from "../../../utils/api/authentication/signin";
import Cookies from "universal-cookie";
import { AuthContext } from "../../../context/authContext";

export const SignIn = () => {
	const cookies = new Cookies();
	const navigate = useNavigate();
	const { setIsAuthenticated } = useContext(AuthContext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [formDetails, setFormDetails] = useState({
		email: "",
		password: "",
	});
	const [isPasswordValid, setIsPasswordValid] = useState(true);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await signInUser(formDetails);
			if (response.status === "success") {
				setIsLoading(false);
				cookies.set("TOKEN", response.token, {
					path: "/",
				});
				setIsAuthenticated(true);
				navigate("/dashboard");
			} else {
				setIsLoading(false);
				setError(
					"Authentication failed. Please check your credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			if (error.message === "Password expired. Please reset your password.") {
				return setIsPasswordValid(false);
			}
			setError(`Login failed. ${error.message}`);
			console.error("Login failed:", error);
		}
	};

	const handleNavigateToResetPassword = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/reset-password");
	};

	return (
		<AuthLayout>
			{isPasswordValid ? (
				<SignInWrapper onSubmit={handleSubmit}>
					<Box>
						<BaseLegend>Welcome back!</BaseLegend>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={400}
							fontSize={"14px"}
							lineHeight={"normal"}
							color="#636364"
							textAlign={"center"}
							whiteSpace={"normal"}
						>
							Welcome back! Please enter your details.
						</Typography>
					</Box>
					<BaseFieldSet>
						<BaseLabel fontsize="14px" fontweight={700} colour="#181818">
							Email
						</BaseLabel>
						<BaseInput
							required
							type="email"
							name="email"
							value={formDetails.email}
							placeholder="Enter your email"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel fontsize="14px" fontweight={700} colour="#181818">
							Password
						</BaseLabel>
						<BaseInput
							required
							type="password"
							name="password"
							value={formDetails.password}
							placeholder="Enter your password"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"#FF0000"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box component={"div"} className="call-to-action">
						<BaseButton
							type="submit"
							variant="contained"
							sx={{
								width: "100%",
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Sign in
								</Typography>
							)}
						</BaseButton>
					</Box>
				</SignInWrapper>
			) : (
				<PasswordExpiredWrapper>
					<Box>
						<BaseLegend>Password Expired</BaseLegend>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={400}
							fontSize={"14px"}
							lineHeight={"normal"}
							color="#636364"
							textAlign={"center"}
							whiteSpace={"normal"}
						>
							Your Password has expired, Please reset password to log back in.
						</Typography>
					</Box>
					<Box component={"div"} className="call-to-action">
						<BaseButton
							variant="contained"
							sx={{
								width: "100%",
							}}
							onClick={handleNavigateToResetPassword}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Reset Password
							</Typography>
						</BaseButton>
					</Box>
				</PasswordExpiredWrapper>
			)}
		</AuthLayout>
	);
};
